import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  SafeAreaView,
  useWindowDimensions,
  Platform,
} from "react-native";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import { ActivityIndicator, Card } from "react-native-paper";
import CustomInput from "../../../components/CustomInput";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import { useEmploymentContext } from "../../../hooks/useEmploymentContext";
import { showMessage } from "react-native-flash-message";
import { useTranslation } from "react-i18next";
import { useTableHelper } from "../../../hooks/useTableHelper";

const FunctionListView = React.memo(
  ({ item, index, handleEditPress, handleDeleteContext, styles }) => {
    return (
      <SafeAreaView style={styles.row}>
        <SafeAreaView
          style={[styles.item, { justifyContent: "flex-start" }]}
        >
          <CustomInput
            value={item.context}
            editable={true}
            type="MAININPUT"
            setValue={(e) => handleEditPress(index, item, "context", e)}
            maxLen={70}
          />
        </SafeAreaView>
        <SafeAreaView style={[styles.item]}>
          <CustomInput
            value={item.description}
            editable={true}
            type="MAININPUT"
            setValue={(e) => handleEditPress(index, item, "description", e)}
            maxLen={70}
          />
        </SafeAreaView>
        <SafeAreaView>
          <CustomButton
            iconName="trash-outline"
            onPress={() => {
              handleDeleteContext(item, index);
            }}
            show="HOME_BELL"
            type="HOME_BELL"
            textTop="2"
            sz={30}
            style={styles.icon}
          />
        </SafeAreaView>
      </SafeAreaView>
    );
  }
);

const HRAdminEmplContextScreen = ({ selectedCompany }) => {
  const { width, height } = useWindowDimensions();
  const { colors } = useTheme();
  const styles = makeStyles(colors, width, height);
  const common = createCommonStyles(colors);
  const [initialState, setInitialState] = useState([]);
  const {
    error,
    getEmploymentContextData,
    updateContext,
    insertContext,
    deleteContext,
  } = useEmploymentContext();
  const [contextData, setContextData] = useState([]);

  const [changes, setChanges] = useState({});
  const [contextsCnt, setContextsCnt] = useState(0);
  const [isDuplicate, setIsDuplicate] = useState([]);
  const [refreshAfterSave, setRefreshAfterSave] = useState(false);
  const [hasNotProvidedCode, setHasNotProvidedCode] = useState();
  const [contextButtonStates, setContextButtonStates] = useState({});
  const [noChangesContext, setNoChangesContext] = useState([]);
  const [insertData, setInsertData] = useState([]);
  const { rowEdit, rowAdd } = useTableHelper(); // * Language
  const [isLoading, setIsLoading] = useState(true);
  const ref = useRef(null);
  const companyRef = useRef(null);

  // * Language
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!refreshAfterSave && companyRef.current === selectedCompany) return;

    companyRef.current = selectedCompany;
    setIsLoading(true); // Set loading to true before fetching data

    getEmploymentContextData(selectedCompany)
      .then((arr) => {
        setContextData([...arr]);
        setInitialState([...arr]); // Set initial state
        setContextsCnt(arr.length);
        setChanges({});
        setNoChangesContext([...arr]);
        setHasNotProvidedCode(Array(arr.length).fill(false));
        setContextButtonStates(Array(arr.length).fill(false));
        setIsLoading(false); // Set loading to false after data is fetched
      })
      .catch((err) => {
        console.error("Error fetching employment context:", err);
        setRefreshAfterSave(false);
        setIsLoading(false);
      });
  }, [selectedCompany, refreshAfterSave]);

  useEffect(() => {
    // Log contextData whenever it changes
  }, [setChanges, changes, contextData]);

  const handleSave = async () => {
    let insertContexts = [];
    let updateExistingContext = [];
    let deleteContexts = [];
    let insertContextDupl = [];
    let updateContextDupl = [];
    let newPromises = [];
    let deleteSucc = { status: true, message: "" };
    let insertSucc = true;
    let updateSucc = true;
    let updatedArray = [...hasNotProvidedCode];
    let emptyCodeFound = false;

    // Update hasNotProvidedCode array based on changes
    Object.entries(changes).forEach(([index, context]) => {
      if (context.context === "") {
        updatedArray[context.index] = true;
        emptyCodeFound = true;
      } else {
        updatedArray[context.index] = false;
      }
    });

    setHasNotProvidedCode([...updatedArray]);

    // Process changes to determine which emloyment_context to insert, update, or delete
    Object.values(changes).forEach((change) => {

      const context = {
        context: change.context,
        company: selectedCompany,
        description: change.description,
        oldEmploymentContextID: change.oldEmploymentContextID,
        rowguid: change.rowguid,
      };

      if (insertData.includes(change.rowguid)) {
        insertContexts.push(context);
      } else if (change.isDeleted === true) {
        deleteContexts.push(context);
      }

      else {
        if (!change.isDeleted) {
          updateExistingContext.push(context);
          updateContextDupl.push(change);
        }
      }
    });

    let duplicates = checkForDuplicates(insertContextDupl, updateContextDupl);
    if (duplicates.length > 0) {
      handleDuplicates(duplicates);
      return;
    }

    if (emptyCodeFound) {
      showMessage({
        message: t("common:mandatoryCodeNotGivenContext"),
        type: "danger",
        duration: 3000,
      });
      return;
    }
    if (updateExistingContext.length > 0) {
      newPromises.push(updateContext(updateExistingContext, updateSucc));

      updateContext(updateExistingContext, updateSucc)
        .then((res) => {
          if (!res) updateSucc = false;
          removeFromChangesAfterSave(updateExistingContext);
          if (res) console.log("update approved");
          if (!res) console.log("update not approved");
        })
        .catch(() => { });
    }

    console.log(insertData);

    if (insertContexts.length > 0) {
      newPromises.push(insertContext(insertContexts, insertSucc));
      insertContext(insertContexts, insertSucc)
        .then((res) => {
          if (!res) insertSucc = false;
          removeFromChangesAfterSave(insertContexts);
        })
        .catch(() => { });
    }
    if (deleteContexts.length > 0) {
      newPromises.push(deleteContext(deleteContexts, deleteSucc));
      deleteContext(deleteContexts, deleteSucc)
        .then((res) => {
          if (!res) {
            deleteSucc["status"] = false;
            deleteSucc["message"] = error.message;
          }
          removeFromChangesAfterSave(deleteContexts);
        })
        .catch(() => { });
    }
    try {
      await Promise.all(newPromises).then(() => {
        if (!insertSucc || !deleteSucc["status"] || !updateSucc) {
          if (!deleteSucc["status"] && deleteSucc["message"] !== "") {
            showMessage({
              message: t("common:contextWithLocationDeleteMess"),
              type: "danger",
              duration: 3000,
            });
            let removeDeleteContexts = Object.fromEntries(
              Object.entries(changes).filter(
                ([key, value]) => value.isDeleted !== true
              )
            );
            setChanges(removeDeleteContexts);
          } else
            showMessage({
              message: t("common:issueMessage"),
              type: "error",
              duration: 3000,
            });
        } else if (insertSucc && deleteSucc["status"] && updateSucc) {
          showMessage({
            message: t("common:allOperationsSuccMess"),
            type: "success",
            duration: 3000,
          });
          setRefreshAfterSave(true);
          setInitialState([...contextData]); // Update initialState after successful operations
        }
      });
    } catch (error) {
      console.log(error);
      setRefreshAfterSave(true);
    }
  };

  const removeFromChangesAfterSave = (contextSavedChanges) => {
    Object.entries(changes).forEach(([index, context]) => {
      contextSavedChanges.forEach((contextSaved) => {
        if (context.index == contextSaved.index) {
          delete changes[context.index];
        }
      });
    });
  };

  const checkForDuplicates = (insertData, updateData) => {
    let duplicateCodes = [];

    const setNoChangesContext = new Set(
      noChangesContext.map((obj) => obj.context?.toUpperCase() ?? "")
    );

    insertData.forEach((insertContext) => {
      const ctx = insertContext.context?.toUpperCase() ?? "";
      if (setNoChangesContext.has(ctx)) {
        insertContext.status = "INSERT";
        insertContext.index = insertContext.index;
        duplicateCodes.push(insertContext);
      } else if (isDuplicate[insertContext.index]) {
        isDuplicate[insertContext.index] = false;
      }
    });

    updateData.forEach((updateContext) => {
      const currentCtx = updateContext.context?.toUpperCase() ?? "";
      const oldCtx = updateContext.oldEmploymentContextID?.toUpperCase() ?? "";

      if (updateContext.isDeleted) return;

      if (setNoChangesContext.has(currentCtx) && oldCtx !== currentCtx) {
        updateContext.status = "UPDATE";
        updateContext.index = updateContext.index;
        duplicateCodes.push(updateContext);
      } else if (oldCtx !== currentCtx && isDuplicate[updateContext.index]) {
        isDuplicate[updateContext.index] = false;
      }
    });

    return duplicateCodes;
  };

  const handleDuplicates = (duplicates) => {
    showMessage({
      message: t("common:duplicateContextMessage"),
      type: "error",
      duration: 3000,
    });
    let prevStateDuplicate = isDuplicate;
    duplicates.forEach((duplicate) => {
      prevStateDuplicate[duplicate.index] = true;
    });
    setIsDuplicate([...prevStateDuplicate]);
    return;
  };

  const handleEditPress = useCallback((index, item, key, value) => {
    if (item[key] === value) return;
    rowEdit(
      index,
      item,
      key,
      value,
      changes,
      setChanges,
      contextData,
      setContextData
    );
  }, []);

  const renderData = (item, index) => {
    return (
      <Card style={common.cardstyle}>
        <Card.Content>
          <FunctionListView
            item={item}
            index={index}
            handleEditPress={handleEditPress}
            handleDeleteContext={handleDeleteContext}
            styles={styles}
          />
        </Card.Content>
      </Card>
    );
  };
  const renderContext = useCallback(
    (item, index) => {
      return <Card.Content>{renderData(item, index)}</Card.Content>;
    },
    [contextData, changes]
  );

  const handleAddNewContext = () => {
    const hasEmptyLine = contextData.some(
      (context) => context.context === "" && context.description === ""
    );

    if (hasEmptyLine) {
      showMessage({
        message: t("common:multEmptyLines"),
        type: "danger",
        duration: 10000,
      });
    } else {
      const newItem = {
        context: "",
        company: selectedCompany,
        description: "",
        isNewContext: true,
        oldEmploymentContextID: "",
        index: contextsCnt,
        rowguid: "",
      };


      rowAdd(
        contextData,
        setContextData,
        [],
        [],
        ref,
        t,
        [],
        [],
        changes,
        setChanges,
        insertData,
        setInsertData,
        (id = "rowguid")
      );
    }
  };

  const handleCancelChanges = () => {
    setContextData([...initialState]);
    setChanges({});
    setIsDuplicate(Array(noChangesContext.length).fill(false));
    setHasNotProvidedCode(Array(noChangesContext.length).fill(false));
  };

  const scrollToTop = () => {
    ref.current?.scrollToOffset({ y: 0, animated: true });
  };

  const handleDeleteContext = (context, index) => {
    if (!(context.index in changes)) {
      setChanges((prevState) => ({
        ...prevState,
        [context.index]: {
          context: context.context,
          company: selectedCompany,
          description: context.description,
          isDeleted: true,
        },
      }));
    } else {
      changes[context.index]["isDeleted"] = true;
    }

    // Clear error/duplicate flags
    if (isDuplicate[context.index]) {
      const updated = [...isDuplicate];
      updated[context.index] = false;
      setIsDuplicate(updated);
    }
    if (hasNotProvidedCode[context.index]) {
      const updated = [...hasNotProvidedCode];
      updated[context.index] = false;
      setHasNotProvidedCode(updated);
    }

    // Delete by unique context string
    const filtered = contextData.filter(
      (item) => item.context !== context.context
    );
    setContextData(filtered);
    setContextsCnt(filtered.length);
  };


  if (isLoading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator animating={true} color={colors.blue} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.loader}>
        <Text style={styles.error}>{t("common:errorLoadingData")}</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.mainView}>
      <SafeAreaView
        style={[
          styles.buttonsContainer,
        ]}
      >
        <CustomButton
          text={t("common:new")}
          onPress={() => {
            handleAddNewContext(selectedCompany);
          }}
          type="BUTTON_ADD"
          textTop="0"
          sz={15}
          style={styles.buttons}
        />
        <CustomButton
          text={t("common:Save")}
          onPress={() => {
            handleSave();
          }}
          type="BUTTON_SAVE"
          textTop="0"
          isGradient={true}
          sz={15}
          style={styles.buttons}
        />

        <CustomButton
          text={t("common:cancel")}
          onPress={() => {
            handleCancelChanges();
          }}
          type="BUTTON_CANCEL"
          textTop="0"
          sz={15}
          style="height:10%"
        />
      </SafeAreaView>
      <FlatList
        data={contextData}
        keyExtractor={(item, index) => `${item.rowguid}-${index}`}
        renderItem={({ item, index }) => renderContext(item, index)}
        refreshing={isLoading}
        ListHeaderComponent={
          contextData.length > 0 && (
            <SafeAreaView style={common.listTitle}>
              <View style={[styles.row, { marginRight: "7%" }]}>
                <CustomLabel value={t("common:Context")} type="SUBTITLE" />
                <CustomLabel value={t("common:Description")} type="SUBTITLE" />
              </View>
               <SafeAreaView style={common.divider}/>
            </SafeAreaView>
          )
        }
        ListEmptyComponent={
          <CustomLabel value={t("common:noContextData")} type="NO_DATA" />
        }
      />
    </SafeAreaView>
  );
};

const makeStyles = (colors, width, height) =>
  StyleSheet.create({
    buttonsContainer: {
      flexDirection: "row",
      justifyContent: "right",
      padding: 0,
    },
    mainView: {
      flex: 1,
      marginHorizontal: 10,
    },
    input: {
      borderWidth: 1,
      borderColor: colors.inputBorder,
      borderRadius: 5,
      padding: 10,
      backgroundColor: colors.inputBackground,
    },

    row: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
    },
    item: {
      flex: 1,
      marginHorizontal: 5,
    },
    error: {
      color: colors.validation_error,
      alignSelf: "center",
      fontSize: 14,
    },
    loader: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "4%",
    },
    buttons: {
      flexDirection: "row",
      justifyContent: "flex-end",
      alignSelf: "flex-end",
    },
  });
export default HRAdminEmplContextScreen;
